import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-secondary-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Privacy Policy</h1>
        <p className="text-secondary-500 text-sm mb-8">Last updated: February 2026</p>

        <div className="prose prose-secondary max-w-none space-y-6 text-secondary-700">
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">1. Introduction</h2>
            <p>
              myoneqr (“we,” “our,” or “us”) operates the myoneqr platform. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website and services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">2. Information We Collect</h2>
            <p>
              We collect account information (name, email, password hash), profile information (image, display name, tagline, location, phone, bio, profile URL, links, collaborations), payment and subscription data via Razorpay (we do not store card details), support messages when you contact us, and technical/usage data (e.g. IP, browser, profile views and link clicks) for analytics and security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">3. How We Use Your Information</h2>
            <p>
              We use the information to create and manage your account and subscription, provide and improve the Service (e.g. displaying your profile, QR codes, analytics), process payments via Razorpay, respond to support requests, send account and billing communications, enforce our Terms of Service, prevent abuse, and comply with applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">4. Cookies and Similar Technologies</h2>
            <p>
              We use cookies for authentication and security. We store access and refresh tokens in secure, HttpOnly cookies so you can stay logged in. These are essential for the Service. We do not use them for third-party advertising on our site.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">5. Sharing of Information</h2>
            <p>
              We share necessary payment and subscription data with Razorpay to process payments. We may use service providers (e.g. hosting, email) who process data on our behalf under strict confidentiality. We may disclose information if required by law or to protect rights and safety. We do not sell your personal information to third parties for their marketing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">6. Data Retention and Security</h2>
            <p>
              We retain account and profile data while your account is active. After you delete your account, we may retain certain data for a limited period for legal, security, or operational purposes. We use industry-standard measures to protect your data, including hashed passwords, HTTPS, and secure storage. You are responsible for keeping your password confidential.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">7. Your Rights and Children</h2>
            <p>
              Depending on your location, you may have the right to access, correct, or delete your data. You can update profile and account details in the dashboard and delete your account in Account settings. The Service is not intended for users under 13; we do not knowingly collect data from children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">8. Changes and Contact</h2>
            <p>
              We may update this Privacy Policy. We will post the updated version on our website and update the “Last updated” date. For privacy-related questions or requests, contact us via the support email or phone listed on the website.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-secondary-200">
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            ← Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
